/**
 * ChatHome — shell de 2 abas: Conversas | Alertas.
 *
 * A aba "Atendente IA" foi aposentada: o @Atendente agora é inline (mencione
 * @Atendente em qualquer conversa) — não há mais espaço privado dedicado.
 *
 * - Aba padrão: Alertas
 * - Aba ativa sincronizada com ?tab= na URL
 * - O tempo real (mensagens + alertas) fica aqui para manter realtime ativo em
 *   qualquer aba, via SSE (useChatRealtime reusa a conexão única do feed de
 *   notificações). O WebSocket legado foi removido na Fase 3 WS→SSE.
 * - Busca global de mensagens no topo: quando há termo (>=2 chars), substitui o
 *   conteúdo das abas por GlobalSearchResults (client-side, via messageStore).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Tabs, TextInput } from '@mantine/core'
import { IconMessages, IconBellRinging, IconSearch } from '@tabler/icons-react'
import { useDebouncedValue } from '@mantine/hooks'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useChatRealtime } from '@/hooks/useChatRealtime'
import { searchMessages, type SearchHit } from '@/lib/messageSearch'
import type { ConversationListItem } from '@/api/chat'
import type { HumanConversation } from '@/api/chatDirectory'
import AlertasTab from './components/AlertasTab'
import ConversasTab from './components/ConversasTab'
import GlobalSearchResults from './components/GlobalSearchResults'

const VALID = ['conversas', 'alertas'] as const

const MIN_QUERY = 2

export default function ChatHome() {
  // Tempo real do chat/alertas via SSE (uma conexão, reusa o SharedWorker do feed).
  useChatRealtime()
  const [sp, setSp] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const raw = sp.get('tab')
  const tab = (VALID as readonly string[]).includes(raw ?? '') ? (raw as string) : 'alertas'

  const setTab = (v: string | null) => {
    const next = new URLSearchParams(sp)
    next.set('tab', v ?? 'alertas')
    setSp(next, { replace: true })
  }

  // Busca global de mensagens (client-side).
  const [search, setSearch] = useState('')
  const [debounced] = useDebouncedValue(search, 250)
  const [hits, setHits] = useState<SearchHit[]>([])

  const query = debounced.trim()
  const searchActive = query.length >= MIN_QUERY

  // Guarda a query mais recente para descartar resultados obsoletos (uma busca
  // antiga que resolve depois não pode sobrescrever uma mais nova).
  const latestQueryRef = useRef('')

  useEffect(() => {
    latestQueryRef.current = query
    if (query.length < MIN_QUERY) {
      setHits([])
      return
    }
    let cancelled = false
    void searchMessages(query).then((results) => {
      // Só aplica se ainda for a query corrente e o efeito não foi descartado.
      if (cancelled || latestQueryRef.current !== query) return
      setHits(results)
    })
    return () => {
      cancelled = true
    }
  }, [query])

  // Resolve o título da conversa combinando OS DOIS caches de conversas:
  //   - ['conversations']            → conversas de alerta (useConversations)
  //   - ['chat-human-conversations'] → DMs/grupos (useHumanConversations)
  // Sem o cache de humanas, hits de DM/org caíam no fallback "Conversa #id".
  // Rebuild só quando algum dos caches muda (searchActive dispara a leitura),
  // memoizado por id→título. Fallback amigável quando nenhum cache tem o id.
  const alertConvs = queryClient.getQueryData<ConversationListItem[]>(['conversations'])
  const humanConvs = queryClient.getQueryData<HumanConversation[]>(['chat-human-conversations'])

  const titleById = useMemo(() => {
    const map = new Map<number, string>()
    for (const c of alertConvs ?? []) {
      if (c && typeof c.id === 'number' && c.title) map.set(c.id, c.title)
    }
    for (const c of humanConvs ?? []) {
      if (c && typeof c.id === 'number' && c.title) map.set(c.id, c.title)
    }
    return map
  }, [alertConvs, humanConvs])

  const resolveTitle = useCallback(
    (conversationId: number): string =>
      titleById.get(conversationId) ?? `Conversa #${conversationId}`,
    [titleById],
  )

  const openHit = (convId: number, msgId: number) => {
    navigate(`/chat/${convId}`, { state: { scrollToMessageId: msgId } })
  }

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Campo de busca global — sempre visível no topo. */}
      <Box px="md" pt="md" pb="xs" style={{ flexShrink: 0 }}>
        <TextInput
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          placeholder="Buscar mensagens…"
          aria-label="Buscar mensagens"
        />
      </Box>

      {searchActive ? (
        <GlobalSearchResults
          hits={hits}
          query={query}
          resolveTitle={resolveTitle}
          onOpen={openHit}
        />
      ) : (
        <Tabs
          value={tab}
          onChange={setTab}
          keepMounted={false}
          style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
        >
          <Tabs.List grow style={{ flexShrink: 0 }}>
            <Tabs.Tab value="conversas" leftSection={<IconMessages size={16} />}>
              Conversas
            </Tabs.Tab>
            <Tabs.Tab value="alertas" leftSection={<IconBellRinging size={16} />}>
              Alertas
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="conversas" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ConversasTab />
          </Tabs.Panel>

          <Tabs.Panel value="alertas" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <AlertasTab />
          </Tabs.Panel>
        </Tabs>
      )}
    </Box>
  )
}
